/* eslint-disable jsdoc/check-indentation */
import styled from '@emotion/styled';
import { Fragment, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as DeprecatedCloseFilledIcon } from '../../assets/icons/DeprecatedCloseFilledIcon.svg';
import { theme } from '../../theme';

export interface DropdownSearchItem<T = unknown> {
  title: string;
  description?: string;
  searchables: (string | null)[];
  additionalData: T;
}

interface DropdownSearchProps<T = unknown> {
  placeholder: string;
  searchableItems: DropdownSearchItem<T>[];
  selectedItem?: DropdownSearchItem<T>;
  onSelectedItemChange: (selectedItem: DropdownSearchItem<T> | undefined) => void;
  className?: string;
  dataIcom: string;
  noResultsText?: string;
}

/**
 * A searchable dropdown component that allows searching through a list of items.
 *
 * This component displays a text input that, when focused, shows a dropdown list of items
 * matching the typed search text. The search supports diacritics-insensitive matching and word order reversals
 * (e.g., "Martin Černý" will match "Černý Martin").
 *
 * @template T - The type of additional data attached to each item
 *
 * @param {DropdownSearchProps<T>} props
 * @param {string} props.placeholder - Placeholder text displayed in the input when empty
 * @param {DropdownSearchItem<T>[]} props.searchableItems - Array of items that can be searched through
 *        Each item contains:
 *        - `title`: The main display text for the item (always shown in dropdown)
 *        - `description`: Optional secondary text to display below the title
 *        - `searchables`: Array of strings to search against, with special matching priority:
 *          - Items at lower indexes have higher priority in search results
 *          - First element (index 0) gets highest priority in sorting results
 *          - Word order matters for matching and can affect result sorting
 *        - `additionalData`: Custom data of type T associated with this item
 * @param {DropdownSearchItem<T>} [props.selectedItem] - Currently selected item
 * @param {Function} props.onSelectedItemChange - Callback when selection changes
 * @param {string} [props.className] - Optional CSS class name for the component wrapper
 * @param {string} props.dataIcom - Data attribute used for identifying the component in tests
 * @param {string} [props.noResultsText] - Text displayed when no results match the search
 *
 * @returns {JSX.Element} The dropdown search component
 *
 * @example
 * // Basic usage example:
 * <DropdownSearch
 *   placeholder="Search users..."
 *   searchableItems={[
 *     {
 *       title: "John Smith",
 *       // First element has higher priority in searching
 *       searchables: ["John Smith", "john.smith@example.com", "Team: Marketing"],
 *       additionalData: { id: "123", type: "user" }
 *     },
 *     // More items...
 *   ]}
 *   onSelectedItemChange={(item) => console.log(item)}
 *   dataIcom="user-search-dropdown"
 *   noResultsText="No users found"
 * />
 */
export const DropdownSearch = <T,>({
  placeholder,
  searchableItems,
  selectedItem,
  onSelectedItemChange,
  className,
  dataIcom,
  noResultsText,
}: DropdownSearchProps<T>) => {
  const [searchText, setSearchText] = useState<string>('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  const handleItemClick = (item: DropdownSearchItem<T>) => {
    onSelectedItemChange(item);
    setSearchText('');
    setFocusedIndex(-1);
  };

  const handleClearSelection = () => {
    onSelectedItemChange(undefined);
    setSearchText('');
    setFocusedIndex(-1);
  };

  const formattedSearchableItems = useMemo(() => {
    return searchableItems.map((item) => ({
      ...item,
      searchablesFormatted: item.searchables
        .filter((n) => n)
        .flatMap((searchable) => {
          const normalized = removeDiacritics(searchable!).toLowerCase();
          // There could be names and we want to be able to search for them in reverse order
          if (normalized.includes(' ')) {
            const reversed = normalized.split(' ').reverse().join(' ');
            return [normalized, reversed];
          }
          return [normalized];
        }),
    }));
  }, [searchableItems]);

  const formattedSearchText = removeDiacritics(searchText).toLowerCase();

  const filteredItems = useMemo(() => {
    if (!formattedSearchText) return [];

    const filtered = formattedSearchableItems.filter((item) =>
      item.searchablesFormatted.some((searchable) => searchable.includes(formattedSearchText)),
    );

    // Really hard to implement sorting function that works well with all cases
    // It sorts based on a lot of different factors to get the best match up top
    // 1. If there is a match in the 0 index of searchables, it should be prioritized
    // 2. If there is a match at the start of the string, it should be prioritized
    // 3. If there is a match at the start of a word (but not the whole string), it should be prioritized
    // 4. If there is one match where it matches AR in word mARtin, it should be higher than a match in word guitAR
    return filtered.sort((a, b) => {
      // Get detailed match information for both items
      const aMatches = getMatchDetails(a.searchables, formattedSearchText);
      const bMatches = getMatchDetails(b.searchables, formattedSearchText);

      // Compare the match details:

      if (aMatches.bestIndex !== bMatches.bestIndex) {
        return aMatches.bestIndex - bMatches.bestIndex;
      }

      if (aMatches.startsWithMatch !== bMatches.startsWithMatch) {
        return aMatches.startsWithMatch ? -1 : 1;
      }

      if (aMatches.startsWithWordMatch !== bMatches.startsWithWordMatch) {
        return aMatches.startsWithWordMatch ? -1 : 1;
      }

      if (aMatches.positionInStr !== bMatches.positionInStr) {
        return aMatches.positionInStr - bMatches.positionInStr;
      }

      return 0;
    });
  }, [formattedSearchableItems, formattedSearchText]);

  // Reset focused index when filtered items change
  useEffect(() => {
    setFocusedIndex(-1);
    // Reset refs array when items change
    listItemsRef.current = listItemsRef.current.slice(0, filteredItems.length);
  }, [filteredItems.length]);

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!filteredItems.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prevIndex) => {
          const nextIndex = prevIndex < filteredItems.length - 1 ? prevIndex + 1 : 0;
          scrollIntoView(nextIndex);
          return nextIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prevIndex) => {
          const nextIndex = prevIndex > 0 ? prevIndex - 1 : filteredItems.length - 1;
          scrollIntoView(nextIndex);
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredItems.length) {
          handleItemClick(filteredItems[focusedIndex]);
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        e.preventDefault();
        inputRef.current?.blur();
        setFocusedIndex(-1);
        break;
    }
  };

  // Scroll the focused item into view if needed
  const scrollIntoView = (index: number) => {
    if (index >= 0 && listItemsRef.current[index] && listRef.current) {
      const item = listItemsRef.current[index];
      const list = listRef.current;

      const itemRect = item!.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();

      if (itemRect.bottom > listRect.bottom) {
        list.scrollTop += itemRect.bottom - listRect.bottom;
      } else if (itemRect.top < listRect.top) {
        list.scrollTop -= listRect.top - itemRect.top;
      }
    }
  };

  return (
    <StyledDropdownSearchWrapper className={className} data-icom={dataIcom}>
      <SearchInputWrapper>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder={selectedItem?.title ?? placeholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          itemSelected={!!selectedItem}
          onKeyDown={handleKeyDown}
        />
        {selectedItem && (
          <ClearButton onClick={handleClearSelection}>
            <DeprecatedCloseFilledIcon />
          </ClearButton>
        )}
      </SearchInputWrapper>
      {searchText.length > 0 && (
        <DropdownList className="dropdown-list" ref={listRef}>
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <DropdownListItem
                key={item.title + item.searchables.join()}
                onClick={() => handleItemClick(item)}
                tabIndex={0}
                isFocused={index === focusedIndex}
                ref={(el) => (listItemsRef.current[index] = el)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                <ItemTitle>{highlightMatch(item.title, formattedSearchText)}</ItemTitle>
                <ItemDescription>
                  {item.description ||
                    highlightMatch(getMatchingSearchable(item, formattedSearchText) || '', formattedSearchText)}
                </ItemDescription>
              </DropdownListItem>
            ))
          ) : (
            <DropdownListItem>{noResultsText}</DropdownListItem>
          )}
        </DropdownList>
      )}
    </StyledDropdownSearchWrapper>
  );
};

// returns a lot of details about the match, in what searchable it was found and other stuff used to compare what match is the best
const getMatchDetails = (searchables: (string | null)[], searchText: string) => {
  let bestIndex = Infinity;
  let startsWithMatch = false;
  let startsWithWordMatch = false;
  let positionInStr = Infinity;

  searchables.forEach((searchable, index) => {
    if (!searchable) return;

    const normalizedSearchable = removeDiacritics(searchable).toLowerCase();
    const matchPosition = normalizedSearchable.indexOf(searchText);

    if (matchPosition === -1) return;

    if (index < bestIndex) {
      bestIndex = index;
      startsWithMatch = matchPosition === 0;
      positionInStr = matchPosition;

      startsWithWordMatch =
        startsWithMatch || normalizedSearchable.split(' ').some((word) => word.startsWith(searchText));
    } else if (index === bestIndex) {
      if (matchPosition === 0 && !startsWithMatch) {
        startsWithMatch = true;
        positionInStr = matchPosition;
      }

      const isWordStart = normalizedSearchable.split(' ').some((word) => word.startsWith(searchText));

      if (isWordStart && !startsWithWordMatch) {
        startsWithWordMatch = true;
      }

      if (matchPosition < positionInStr) {
        positionInStr = matchPosition;
      }
    }
  });

  return {
    bestIndex: bestIndex === Infinity ? 999 : bestIndex,
    startsWithMatch,
    startsWithWordMatch,
    positionInStr: positionInStr === Infinity ? 999 : positionInStr,
  };
};

export const removeDiacritics = (text: string) => {
  if (!text) {
    return text;
  }
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const highlightMatch = (text: string, match: string) => {
  if (!match) return text;

  const normalizedText = removeDiacritics(text).toLowerCase();

  if (normalizedText.includes(' ') && match.includes(' ')) {
    if (normalizedText.includes(match)) {
      return highlightDirectMatch(text, normalizedText, match);
    }

    // If it did no matched in normal order for example Martin Černý won't match with Černý Martin
    // we need to reverse the text and try to match again
    const textWords = normalizedText.split(' ');
    const reversedText = textWords.reverse().join(' ');

    if (reversedText.includes(match)) {
      // If the reversed text contains the match, we need to highlight in the original text
      // by mapping the positions from reversed text back to original text
      // It was a pain in the ass ...
      return highlightReversedMatch(text, textWords, match);
    }

    // Return original text if no match was found
    return text;
  }

  return highlightDirectMatch(text, normalizedText, match);
};

const highlightDirectMatch = (text: string, normalizedText: string, match: string) => {
  // direct match means that the text is the same as the match
  const result: ReactNode[] = [];
  let lastIndex = 0;

  let matchIndex = normalizedText.indexOf(match, lastIndex);
  while (matchIndex !== -1) {
    // Add the text before the match
    if (matchIndex > lastIndex) {
      result.push(text.substring(lastIndex, matchIndex));
    }

    // Add the matched string with original diacritics but highlighted
    const originalTextWithDiacritics = text.substring(matchIndex, matchIndex + match.length);
    result.push(<Bold key={`${matchIndex}-${originalTextWithDiacritics}`}>{originalTextWithDiacritics}</Bold>);

    // Move to the next position after the match and continue - there might be more matches
    lastIndex = matchIndex + match.length;
    matchIndex = normalizedText.indexOf(match, lastIndex);
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result;
};

// Pain in the ass function. I won't even bother commenting it, just don't touch it!
const highlightReversedMatch = (text: string, textWords: string[], match: string) => {
  const matchWords = match.split(' ');
  const originalWords = text.split(' ');

  return originalWords
    .map((word, index) => {
      const normalizedWord = removeDiacritics(word).toLowerCase();

      const matchingPart = matchWords.find((matchWord) => normalizedWord.includes(matchWord));

      if (matchingPart) {
        const matchStart = normalizedWord.indexOf(matchingPart);
        if (matchStart >= 0) {
          const beforeMatch = word.substring(0, matchStart);
          const matchedPart = word.substring(matchStart, matchStart + matchingPart.length);
          const afterMatch = word.substring(matchStart + matchingPart.length);

          return (
            <Fragment key={`word-${index}`}>
              {beforeMatch}
              <Bold>{matchedPart}</Bold>
              {afterMatch}
            </Fragment>
          );
        }
      }

      if (matchWords.some((matchWord) => normalizedWord === matchWord)) {
        return <Bold key={`word-${index}`}>{word}</Bold>;
      }
      return word;
    })
    .reduce((acc: ReactNode[], element, index) => {
      if (index === 0) return [element];
      return [...acc, ' ', element];
    }, []);
};

const getMatchingSearchable = (item: DropdownSearchItem, searchText: string) => {
  // First try to find an exact match excluding the title
  let match = item.searchables.find((searchable) => {
    if (!searchable || searchable === item.title) return false;
    const normalized = removeDiacritics(searchable).toLowerCase();
    return normalized.includes(searchText);
  });

  // If no exact match, try to find a match with reversed word order
  if (!match && searchText.includes(' ')) {
    match = item.searchables.find((searchable) => {
      if (!searchable || searchable === item.title) return false;
      const normalized = removeDiacritics(searchable).toLowerCase();
      if (!normalized.includes(' ')) return false;
      const reversed = normalized.split(' ').reverse().join(' ');
      return reversed.includes(searchText);
    });
  }

  // If still no match, return some searchable that is not the title of the item
  return match || item.searchables.find((searchable) => searchable && searchable !== item.title);
};

const StyledDropdownSearchWrapper = styled.div`
  position: relative;

  .dropdown-list {
    display: none;
  }

  &:focus-within .dropdown-list {
    display: initial;
  }
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input<{ itemSelected: boolean }>`
  width: 100%;
  padding: ${theme.spacing.d} ${theme.spacing.f};
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.m};
  &:focus {
    border-color: ${theme.colors.borderMain.active};
    outline: 0;
  }
  &:not(:focus)::placeholder {
    color: ${({ itemSelected }) => itemSelected && theme.colors.text.primary};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: ${theme.spacing.e};
  background: none;
  border: none;
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: ${theme.borderRadius.l};
  max-height: 307.5px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0px 2px 8px 0px #00000026;
`;

const DropdownListItem = styled.div<{ isFocused?: boolean }>`
  padding: 9px 12px;
  cursor: pointer;
  background: ${({ isFocused }) => (isFocused ? theme.colors.backgroundBasic.hover : 'transparent')};
  &:hover {
    background: ${theme.colors.backgroundBasic.hover};
  }
`;

const ItemTitle = styled.div``;

const ItemDescription = styled.div`
  color: ${theme.colors.text.secondary};
`;

const Bold = styled.b`
  color: ${theme.colors.text.primary};
`;
